import * as Dialog from '@radix-ui/react-dialog';
import type { HTMLAttributes } from 'react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import type { ComponentProps } from '~/chords/ComponentProps';
import { getClassNameOverride } from '~/chords/getClassNameOverride';
import { renderSlot } from '~/chords/utils/renderSlot';

import { ModalDialogBody } from './components/ModalDialogBody';
import { ModalDialogClose } from './components/ModalDialogClose';
import { ModalDialogContent } from './components/ModalDialogContent';
import { ModalDialogFooter } from './components/ModalDialogFooter';
import { ModalDialogHeader } from './components/ModalDialogHeader';
import { ModalDialogTrigger } from './components/ModalDialogTrigger';
import { CONTENT_CLASS, OVERLAY_CLASS } from './components/styles';

export interface ModalDialogProps
  extends Dialog.DialogProps,
    ComponentProps<HTMLAttributes<HTMLDivElement>> {}

export interface ModalDialogRef {
  close: () => void;
}

const ModalDialogComponent = forwardRef<ModalDialogRef, ModalDialogProps>(
  ({ children, ...props }, ref) => {
    const className = getClassNameOverride(props, 'ModalDialog');
    const overlayRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(
      props?.open || props?.defaultOpen || false,
    );

    useEffect(() => {
      if (props.open !== undefined) {
        setIsOpen(props.open);
      }
    }, [props.open]);

    const toggleIsOpen = useCallback(
      (open: boolean) => {
        setIsOpen(open);
        props.onOpenChange?.(open);
      },
      [props],
    );

    const closeModalDialog = useCallback(() => {
      toggleIsOpen(false);
    }, [toggleIsOpen]);

    const handleClose = useCallback(() => {
      overlayRef.current?.classList.remove(OVERLAY_CLASS.show);
      contentRef.current?.classList.remove(CONTENT_CLASS.show);

      // There's a bug in Firefox where the animationend event doesn't fire if the
      // animation class is removed and then added back in the same frame. The effect
      // is that the modal elements are left on top of everything else and the
      // user can't interact with anything. See WMG1-1076 for more details.
      //
      // This is a hack to force a reflow/layout flush of the browser to commit
      // the removal of the old animation class and then treat the new class as
      // a fresh animation. Reference:
      // https://medium.com/@khalidoghli7/how-to-trigger-a-reflow-for-flushing-css-changes-e89c96030c04
      void contentRef.current?.offsetWidth;

      contentRef.current?.addEventListener('animationend', closeModalDialog, {
        once: true,
      });

      requestAnimationFrame(() => {
        // Animate the overlay and dialog out of view as they close.
        overlayRef.current?.classList.add(OVERLAY_CLASS.hide);
        contentRef.current?.classList.add(
          CONTENT_CLASS.disable,
          CONTENT_CLASS.hide,
        );
      });
    }, [overlayRef, contentRef, closeModalDialog]);

    useEffect(() => {
      const dialogContent = contentRef.current;

      return () => {
        if (!isOpen) {
          dialogContent?.removeEventListener('animationend', closeModalDialog);
        }
      };
    }, [contentRef, isOpen, closeModalDialog]);

    useImperativeHandle(
      ref,
      () => ({
        close: handleClose,
      }),
      [handleClose],
    );

    const handleOpenChange = useCallback(
      (open: boolean) => (open ? toggleIsOpen(true) : handleClose()),
      [handleClose, toggleIsOpen],
    );

    return (
      <Dialog.Root {...props} open={isOpen} onOpenChange={handleOpenChange}>
        {renderSlot(ModalDialogTrigger, children)}
        <ModalDialogContent
          overlayRef={overlayRef}
          contentRef={contentRef}
          className={className}
        >
          {children}
        </ModalDialogContent>
      </Dialog.Root>
    );
  },
);

ModalDialogComponent.displayName = 'ModalDialog';

export const ModalDialog = Object.assign(ModalDialogComponent, {
  Trigger: ModalDialogTrigger,
  Header: ModalDialogHeader,
  Body: ModalDialogBody,
  Footer: ModalDialogFooter,
  Close: ModalDialogClose,
});
